import { FormInputField } from '@/components/forms/form-input-field';
import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Check } from 'lucide-react';
import { ReactNode, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

interface PurgeConfirmationGateProps {
  expectedName: string;
  label: string;
  action: string;
  note?: ReactNode;
  disabled?: boolean;
  testId: string;
  onConfirmed: (confirmedName: string) => void;
}

export const PurgeConfirmationGate = ({
  expectedName,
  label,
  action,
  note,
  disabled,
  testId,
  onConfirmed,
}: PurgeConfirmationGateProps) => {
  const [confirmedName, setConfirmedName] = useState<string | null>(null);

  const formSchema = useMemo(
    () =>
      z.object({
        confirmationName: z
          .string()
          .trim()
          .min(1, 'Type the name to confirm.')
          .refine((name) => name === expectedName.trim(), 'Type the name exactly as it appears above to confirm.'),
      }),
    [expectedName],
  );

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: { confirmationName: '' },
    mode: 'onChange',
  });

  const onSubmit = form.handleSubmit(({ confirmationName }) => {
    if (disabled) {
      return;
    }

    setConfirmedName(confirmationName);
    onConfirmed(confirmationName);
  });

  if (confirmedName !== null) {
    return (
      <p className="flex items-center gap-1 text-sm text-gray-600" data-testid={`${testId}-confirmed`}>
        <Check className="h-4 w-4" />
        Confirmed as {confirmedName}
      </p>
    );
  }

  return (
    <Form {...form}>
      <form onSubmit={onSubmit}>
        {note && (
          <p className="mb-3 text-sm text-gray-600" data-testid={`${testId}-note`}>
            {note}
          </p>
        )}
        <div className="flex items-end gap-2">
          <FormInputField
            form={form}
            className="flex-1"
            name="confirmationName"
            label={label}
            inputProps={{
              placeholder: expectedName,
              autoComplete: 'off',
              disabled,
              'data-testid': `${testId}-name`,
            }}
          />
          <Button
            type="submit"
            variant="destructive"
            className="mb-6"
            disabled={disabled || !form.formState.isValid}
            data-testid={`${testId}-submit`}
          >
            {action}
          </Button>
        </div>
      </form>
    </Form>
  );
};
